<template>
  <div class="d-flex align-center justify-center pa-4">
    <!-- Previous Page -->
    <a
      v-if="currentPage > 1"
      :href="getPageUrl(currentPage - 1)"
      class="mx-1 px-3 py-2 text-decoration-none rounded"
      :class="{ 'text-primary': currentPage !== 1 }"
      @click="handleClick($event, currentPage - 1)"
    >
      <v-icon>mdi-chevron-left</v-icon>
    </a>

    <!-- First page -->
    <a
      v-if="showFirstPage"
      :href="getPageUrl(1)"
      class="mx-1 px-3 py-2 text-decoration-none rounded"
      :class="{ 'text-primary': currentPage !== 1 }"
      @click="handleClick($event, 1)"
    >
      1
    </a>

    <!-- Ellipsis after first page -->
    <span v-if="showLeftEllipsis" class="mx-2">...</span>

    <!-- Page numbers -->
    <template v-for="page in visiblePages" :key="page">
      <a
        :href="getPageUrl(page)"
        class="mx-1 px-3 py-2 text-decoration-none rounded"
        :class="{
          'bg-primary text-white': page === currentPage,
          'text-primary': page !== currentPage
        }"
        @click="handleClick($event, page)"
      >
        {{ page }}
      </a>
    </template>

    <!-- Ellipsis before last page -->
    <span v-if="showRightEllipsis" class="mx-2">...</span>

    <!-- Last page -->
    <a
      v-if="showLastPage"
      :href="getPageUrl(totalPages)"
      class="mx-1 px-3 py-2 text-decoration-none rounded"
      :class="{ 'text-primary': currentPage !== totalPages }"
      @click="handleClick($event, totalPages)"
    >
      {{ totalPages }}
    </a>

    <!-- Next Page -->
    <a
      v-if="currentPage < totalPages"
      :href="getPageUrl(currentPage + 1)"
      class="mx-1 px-3 py-2 text-decoration-none rounded"
      :class="{ 'text-primary': currentPage !== totalPages }"
      @click="handleClick($event, currentPage + 1)"
    >
      <v-icon>mdi-chevron-right</v-icon>
    </a>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const props = defineProps({
  currentPage: {
    type: Number,
    required: true
  },
  totalPages: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['page-change'])

const getPageUrl = (page) => {
  const query = {
    ...route.query,
    page: page.toString()
  }
  return router.resolve({
    path: route.path,
    query
  }).href
}

const handleClick = (event, page) => {
  // If CMD/CTRL key is pressed, let the default behavior happen (open in new tab)
  if (event.metaKey || event.ctrlKey) {
    return
  }
  
  // Otherwise prevent default and emit the page change
  event.preventDefault()
  emit('page-change', page)
}

const visiblePages = computed(() => {
  if (props.totalPages <= 5) {
    return Array.from({ length: props.totalPages }, (_, i) => i + 1)
  }

  let start = Math.max(props.currentPage - 1, 1)
  let end = Math.min(start + 4, props.totalPages)

  if (props.currentPage > props.totalPages - 4) {
    start = props.totalPages - 4
    end = props.totalPages
  }

  if (props.currentPage < 5) {
    start = 1
    end = 5
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

const showFirstPage = computed(() => props.currentPage > 4)
const showLastPage = computed(() => props.currentPage < props.totalPages - 2)
const showLeftEllipsis = computed(() => props.currentPage > 4)
const showRightEllipsis = computed(() => props.currentPage < props.totalPages - 3)
</script> 